import { useEffect, useRef, useState } from "react";
import { PrinterIcon } from "@heroicons/react/24/outline";
import { Dialog } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { Alert } from "@/components/ui/Alert";
import { Spinner } from "@/components/ui/Spinner";
import { PdfJsPages } from "@/components/pdf/PdfJsPages";
import { PrintLocationDialog } from "@/features/timesheetFlow/components/PrintLocationDialog";
import { formatDateTime } from "@/lib/format";
import { formatAttachmentSize } from "@/lib/mailAttachments";
import {
  fetchSchwarzesBrettFileBlob,
  schwarzesBrettFileUrl,
  type SchwarzesBrettFile,
} from "@/lib/schwarzesBrett";
import { fileKindMeta } from "@/pages/schwarzesBrett/fileKind";
import { canPrintFile, printSchwarzesBrettFile } from "@/pages/schwarzesBrett/print";

interface FilePreviewDialogProps {
  file: SchwarzesBrettFile | null;
  /** Büro/App-Admins: Link zum Öffnen in SharePoint bzw. Office Online anzeigen. */
  canOpenInSharePoint: boolean;
  onClose: () => void;
}

type PreviewState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "pdf"; blob: Blob }
  | { status: "image"; url: string; blob: Blob }
  | { status: "unsupported" }
  | { status: "error"; message: string };

export function FilePreviewDialog({ file, canOpenInSharePoint, onClose }: FilePreviewDialogProps) {
  const [state, setState] = useState<PreviewState>({ status: "idle" });
  const [printOpen, setPrintOpen] = useState(false);
  const fileRef = useRef(file);
  fileRef.current = file;

  useEffect(() => {
    setPrintOpen(false);
    if (!file) {
      setState({ status: "idle" });
      return;
    }
    if (file.preview === "none") {
      setState({ status: "unsupported" });
      return;
    }

    const controller = new AbortController();
    let objectUrl: string | null = null;
    setState({ status: "loading" });

    const format = file.preview === "convert" ? "pdf" : "original";
    void fetchSchwarzesBrettFileBlob(file.id, format, controller.signal)
      .then((blob) => {
        if (controller.signal.aborted) return;
        if (file.preview === "image") {
          objectUrl = URL.createObjectURL(blob);
          setState({ status: "image", url: objectUrl, blob });
          return;
        }
        const pdfBlob =
          blob.type === "application/pdf" ? blob : new Blob([blob], { type: "application/pdf" });
        setState({ status: "pdf", blob: pdfBlob });
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setState({
          status: "error",
          message: err instanceof Error ? err.message : "Vorschau konnte nicht geladen werden.",
        });
      });

    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [file]);

  const meta = file ? fileKindMeta(file.extension) : null;
  const openLabel = meta?.officeApp ? `In ${meta.officeApp} öffnen` : "In SharePoint öffnen";
  const isConverted = file?.preview === "convert";
  const printable = file ? canPrintFile(file) : false;
  const loadedBlob =
    state.status === "pdf" || state.status === "image" ? state.blob : null;

  const subtitle = file
    ? [
        file.folder_name ? `${file.folder_name}${file.sub_path ? ` / ${file.sub_path}` : ""}` : null,
        file.last_modified_at ? `Geändert ${formatDateTime(file.last_modified_at)}` : null,
        file.last_modified_by || null,
        formatAttachmentSize(Number(file.size) || 0) || null,
      ]
        .filter(Boolean)
        .join(" · ")
    : undefined;

  return (
    <>
      <Dialog
        open={Boolean(file)}
        onClose={onClose}
        title={file?.name || "Vorschau"}
        description={subtitle}
        size="4xl"
        fullScreenMobile
        plainBackdrop
        bodyClassName="flex min-h-0 flex-col bg-fill-quaternary/15"
        footer={
          <div className="flex w-full flex-wrap items-center justify-between gap-2">
            <div className="text-xs text-label-tertiary">
              {isConverted && canOpenInSharePoint
                ? "Schreibgeschützte PDF-Ansicht – zum Ausfüllen oder Bearbeiten bitte in Office öffnen."
                : isConverted
                  ? "Schreibgeschützte PDF-Ansicht."
                  : null}
            </div>
            <div className="flex flex-wrap justify-end gap-2">
              {file ? (
                <>
                  <a
                    href={schwarzesBrettFileUrl(file.id, { download: true })}
                    className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-card px-4 text-sm font-medium text-label ring-1 ring-inset ring-separator-opaque transition hover:bg-fill-tertiary"
                  >
                    Herunterladen
                  </a>
                  {canOpenInSharePoint && file.web_url ? (
                    <a
                      href={file.web_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-card px-4 text-sm font-medium text-label ring-1 ring-inset ring-separator-opaque transition hover:bg-fill-tertiary"
                    >
                      {openLabel}
                    </a>
                  ) : null}
                  {printable ? (
                    <Button
                      type="button"
                      variant="primary"
                      leftIcon={<PrinterIcon className="h-4 w-4" aria-hidden />}
                      disabled={state.status === "loading" || state.status === "error"}
                      title={
                        state.status === "loading"
                          ? "Bitte warten, bis die Vorschau geladen ist"
                          : "Am Druckort (CUPS) drucken"
                      }
                      onClick={() => setPrintOpen(true)}
                    >
                      Drucken
                    </Button>
                  ) : null}
                </>
              ) : null}
              <Button type="button" variant="secondary" onClick={onClose}>
                Schließen
              </Button>
            </div>
          </div>
        }
      >
        <div className="min-h-[min(72vh,56rem)]">
          {state.status === "loading" ? (
            <div className="flex min-h-[min(72vh,56rem)] flex-col items-center justify-center gap-2 text-sm text-label-secondary">
              <Spinner />
              <span>{isConverted ? "Dokument wird in PDF umgewandelt …" : "Vorschau wird geladen …"}</span>
            </div>
          ) : null}
          {state.status === "error" ? (
            <Alert variant="error" title="Vorschau nicht möglich">
              {state.message}
              {canOpenInSharePoint && file?.web_url ? (
                <span className="mt-1 block">Die Datei kann trotzdem in SharePoint geöffnet werden.</span>
              ) : (
                <span className="mt-1 block">Die Datei kann trotzdem heruntergeladen werden.</span>
              )}
            </Alert>
          ) : null}
          {state.status === "unsupported" ? (
            <Alert variant="info" title="Keine Vorschau für diesen Dateityp">
              Bitte die Datei herunterladen{canOpenInSharePoint ? " oder in SharePoint öffnen" : ""}.
            </Alert>
          ) : null}
          {state.status === "pdf" && file ? (
            <PdfJsPages blob={state.blob} title={file.name} className="py-2" />
          ) : null}
          {state.status === "image" && file ? (
            <div className="flex items-start justify-center py-2">
              <img
                src={state.url}
                alt={file.name}
                className="max-h-[80vh] max-w-full rounded-lg border border-separator bg-white object-contain shadow-sm"
              />
            </div>
          ) : null}
        </div>
      </Dialog>

      <PrintLocationDialog
        open={printOpen && Boolean(file)}
        onClose={() => setPrintOpen(false)}
        onPrint={async (locationId, copies) => {
          const current = fileRef.current;
          if (!current) return;
          await printSchwarzesBrettFile(current, locationId, copies, loadedBlob);
        }}
      />
    </>
  );
}
